import { useState, useEffect } from 'react';
import { Card } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Switch } from '../../../../components/ui/Switch';
import { Database, Smartphone, LinkIcon, AlertTriangle, Loader2, type LucideIcon } from 'lucide-react';
import { usePermissions } from '../../../../hooks/usePermissions';
import { settingsApi } from '../../../../services/api/endpoints/settings';
import type { DataSource } from '../../../../types/settings';
import toast from 'react-hot-toast';

// Map icon string names (from API) to Lucide components — API sends PascalCase
const ICON_MAP: Record<string, LucideIcon> = {
  Smartphone: Smartphone,
  smartphone: Smartphone,
  LinkIcon: LinkIcon,
  link: LinkIcon,
  Database: Database,
  database: Database,
};

function getIcon(iconName: string): LucideIcon {
  return ICON_MAP[iconName] ?? ICON_MAP[iconName?.toLowerCase()] ?? Database;
}

export function DataSourceSettings() {
  const { isAdmin, can } = usePermissions();
  const canManageIntegrations = can('manage_integrations') || isAdmin;

  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState<string | null>(null); // sourceId being saved
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await settingsApi.getDataSourceSettings();
        if (!response.success || !response.data) throw new Error();
        setDataSources(response.data.sources);
      } catch {
        setError('Impossible de charger les sources de données. Veuillez réessayer.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const handleToggleSource = async (sourceId: string, isConnected: boolean) => {
    if (!canManageIntegrations || isSaving) return;
    // Optimistic update
    setDataSources(prev =>
      prev.map(s => (s.id === sourceId ? { ...s, isConnected } : s))
    );
    setIsSaving(sourceId);
    setError(null);
    try {
      const response = await settingsApi.updateDataSourceSettings({ sourceId, isConnected });
      if (!response.success) throw new Error();
      toast.success(isConnected ? 'Source connectée' : 'Source déconnectée');
    } catch {
      // Revert optimistic update
      setDataSources(prev =>
        prev.map(s => (s.id === sourceId ? { ...s, isConnected: !isConnected } : s))
      );
      setError('Impossible de mettre à jour la source. Veuillez réessayer.');
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setIsSaving(null);
    }
  };

  if (isLoading) {
    return (
      <Card title="Sources de données" icon={Database}>
        <div className="flex items-center justify-center h-40 gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Chargement des sources de données...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Sources de données" icon={Database}>
      <div className="space-y-6">
        {!canManageIntegrations && (
          <div className="flex items-start gap-3 p-3 rounded-lg border bg-yellow-500/5 border-yellow-500/20 text-sm text-yellow-700 dark:text-yellow-400">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>Les modifications sont réservées aux administrateurs. Vous visualisez la configuration actuelle.</span>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-3 p-3 rounded-lg border bg-destructive/5 border-destructive/20 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          Configurez les sources externes pour enrichir vos données comptables et améliorer les analyses.
        </p>

        <div className="space-y-0">
          {dataSources.map((source: DataSource) => {
            const IconComponent = getIcon(source.icon as string);
            const isSavingThis = isSaving === source.id;
            return (
              <div key={source.id} className="border-t border-border pt-4 pb-3 first:border-t-0 first:pt-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex-shrink-0 h-8 w-8 rounded-md bg-muted flex items-center justify-center">
                      <IconComponent className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-foreground">{source.name}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{source.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isSavingThis && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                    <Switch
                      checked={source.isConnected}
                      onCheckedChange={(checked) => handleToggleSource(source.id, checked)}
                      disabled={!canManageIntegrations || !!isSaving}
                    />
                  </div>
                </div>

                {source.isConfigurable && !source.isConnected && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 ml-11"
                    onClick={() => handleToggleSource(source.id, true)}
                    disabled={!canManageIntegrations || !!isSaving}
                  >
                    {isSavingThis ? (
                      <><Loader2 className="h-3 w-3 animate-spin mr-1.5" /> Configuration...</>
                    ) : (
                      'Configurer la connexion'
                    )}
                  </Button>
                )}

                {source.isConnected && (
                  <div className="mt-2 ml-11 flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                    <span className="h-1.5 w-1.5 bg-green-600 dark:bg-green-400 rounded-full" />
                    Connecté{source.lastSyncDate ? ` • Sync: ${new Date(source.lastSyncDate).toLocaleDateString()}` : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
